import { graphql, PageProps } from 'gatsby';
import React from 'react';
import FranchiseGlobalAbout from '../../components/blocks/FranchiseGlobalAbout';
import FranchiseReviewsSlider from '../../components/sections/FranchiseReviewsSlider';
import { BaseMicrositeData } from '../../contexts/franchise/microsite-context';
import type { FranchiseData } from '../../sourcing/source-nodes/sources/franchises';
import DefaultTemplate from '../default';
import commercialResidentialText from './utils/commercial-residential-text';
import sortTestimonialsByCategory from './utils/sort-testimonials';
import transformDynamic from './utils/transform-dynamic';
import useServiceTypeBlockFilter from './utils/use-service-type-block-filter';
import { PageTemplate } from '../../utils/match-page-template';
import { Testimonial } from './types/testimonial';

export type FranchiseServicePageData = Queries.FranchiseServiceTemplateQuery & {
    allFranchiseTestimonial: {
        nodes: Testimonial[]
    }
    franchise: FranchiseData
};

export interface FranchiseServicePageContext {
    slug: string,
    franchiseNumber: number,
    permalink: string,
    uid: string,
    testimonialCategories: Record<string, number>,
    templateName: PageTemplate.MicrositeService,
}

const FranchiseService: React.FC<PageProps<FranchiseServicePageData, FranchiseServicePageContext>> = ({ data, pageContext, ...props }) => {
    const { testimonialCategories } = pageContext;

    const testimonials = data?.allFranchiseTestimonial?.nodes || [];
    const sortedTestimonials = sortTestimonialsByCategory(testimonials, testimonialCategories);
    const {
        servicesProvidedReadable,
        servicesProvidedFilterable,
        name,
        activeMarkets,
        address,
        profileExcerpt,
    } = data.franchise as FranchiseData;

    const careerPlug = data?.franchise?.aboutUs?.careerPlugUrl;

    const reparsedData = useServiceTypeBlockFilter(transformDynamic(data, {
        locationName: name,
        locationCity: address?.city,
        locationState: address?.state,
        locationServiceAreas: (activeMarkets || []).map(activeMarket => `${activeMarket.city}, ${activeMarket.state}`)?.join(', '),
        locationCompanyProfile: profileExcerpt,
        jobLink: careerPlug,
        servicesProvidedReadable: (servicesProvidedReadable || []).map(x => x.serviceLine),
        servicesProvidedFilterable: (servicesProvidedFilterable || []).map(x => x.serviceLine),
        servicesForPage: (data.cmsFranchiseServicePages?.linked_service_type?.[0]?.franchise_service_mapping || '').split(','),
        propertyType: commercialResidentialText(data as BaseMicrositeData, 'cmsFranchiseServicePages'),
    }), 'cmsFranchiseServicePages') as FranchiseServicePageData;

    if (!reparsedData?.cmsFranchiseServicePages) return null;

    return (
        <DefaultTemplate
            {...props}
            pageContext={pageContext}
            data={{ cmsFranchiseServicePages: reparsedData.cmsFranchiseServicePages }}
        >
            {sortedTestimonials.length > 2 && <FranchiseReviewsSlider data={{ heading: 'Customer Reviews', slides: sortedTestimonials }} />}
            <FranchiseGlobalAbout />
        </DefaultTemplate>
    );
};

export const query = graphql`
    query FranchiseServiceTemplate ($slug: String!, $franchiseNumber: Int!) {
        cmsFranchiseServicePages(url: { eq: $slug }) {
            title
            url
            seo {
                meta_title
                meta_description
                meta_image {
                    url
                }
            }
            linked_service_type {
                franchise_service_mapping
            }
            resolvedSections {
                show_below_if {
                    linked_service_type {
                        franchise_service_mapping
                    }
                }
                ...AboutFranchiseHeroData
                ...ServiceAreaData
                ...ServicesWeProvideData
                ...BlogrollData
                ...CommunityBannerData
                ...ContactBannerData
                ...FaqsData
                ...FranchiseDisclosureData
                ...GalleryData
                ...HeroData
                ...IconBulletsData
                ...IngredientsDisclosureData
                ...InsurerGridData
                ...MediaContentData
                ...MediacontentFaqsData
                ...FaqsGridData
                ...MultiBlogrollData
                ...PartnerLogosData
                ...QuicklinksData
                ...ResourceDetailData
                ...ReviewGridData
                ...RichtextBlockData
                ...ServicesDetailData
                ...ServicesSliderData
                ...SliderData
                ...SplitTextBannerData
                ...SubnavData
                ...TeamMembersData
                ...TestimonialData
                ...TextBannerData
                ...FormData
                ...AnchorData
                ...FranchiseFormHeroData
                ...FranchiseFormCtaData
            }
        }
        allFranchiseUniqueContent(filter: {
            franchiseNumber: {
                eq: $franchiseNumber
            }
        }) {
            nodes {
                approvedDate
                pageName
                content
                rawContent
            }
        }
        franchise(franchiseNumber: { eq: $franchiseNumber }) {
            ...FranchiseMicrositeData
        }
        allFranchiseTestimonial(filter: { franchiseNumber: { eq: $franchiseNumber }}) {
          nodes {
                ...AllFranchiseTestimonialData
          }
        }
    }
`;

export default FranchiseService;
